// The query contract over the resident rows: filter, sort, count, and the ids for a
// rendered range.
//
// It is an interface because the memory budget is an estimate from row-size arithmetic
// rather than a measurement. If the client gate fails, a columnar implementation
// replaces the plain-array one behind this contract and touches no component, no store
// and no sync code. That swap is the only fallback the design has, so the seam matters
// more than either implementation.
//
// `rowsForRange` exists so a caller can never build a view-model for the whole matching
// set. Deriving for everything on every keystroke is the specific behaviour that makes a
// large list stall, so the contract does not offer a way to do it.

import { IssueLifecycleState } from '../models/issue';
import { ContributionSourceType } from '../models/contribution';
import { IssueSyncRow } from '../sync/issue-sync-models';
import { IssueFleetRowVm, IssueLocationState } from './issue-fleet-row';

// Every field narrows. An empty array or an empty string means "no opinion", never
// "match nothing", so a fresh criteria object matches the whole resident set.
export interface IssueFilterCriteria {
  // Matches asset name, VIN or the problem's display name.
  text: string;
  // The problem itself, keyed by fingerprint. Narrower than the category: a category
  // holds many fingerprints, so the two narrow different amounts and are separate
  // controls rather than two levels of one.
  fingerprints: string[];
  categories: string[];
  lifecycleStates: IssueLifecycleState[];
  // Tiers rather than raw numbers: 4 critical, 3 high, 2 medium, 1 low, 0 none.
  severityTiers: number[];
  // The second severity axis. The vehicle's overall rollup is a separate question from
  // this Issue's severity, and the filter set treats them as separate.
  vehicleSeverityTiers: number[];
  locationStates: IssueLocationState[];
  homeLocationGroupIds: string[];
  // The shop the asset is parked in right now, keyed by group id. A different question
  // from the home location: this one moves as the asset moves.
  currentShopGroupIds: string[];
  assignedLocationIds: string[];
  assigneeUserIds: string[];
  // Unassigned is a choice alongside the assignee list, not the absence of one.
  includeUnassigned: boolean;
  // Tenant status ids. 0 means the work has not started, which is a real state rather
  // than a missing value. The caller expands the all-active and all-closed shortcuts
  // into ids before setting criteria — only the case-status reference list knows which
  // ids are terminal, and this layer holds no reference data.
  reopenedOnly: boolean;
  origins: ContributionSourceType[];
  makes: string[];
  models: string[];
  modelYears: string[];
}

export function emptyCriteria(): IssueFilterCriteria {
  return {
    text: '',
    fingerprints: [],
    categories: [],
    lifecycleStates: [],
    severityTiers: [],
    vehicleSeverityTiers: [],
    locationStates: [],
    homeLocationGroupIds: [],
    currentShopGroupIds: [],
    assignedLocationIds: [],
    assigneeUserIds: [],
    includeUnassigned: false,
    reopenedOnly: false,
    origins: [],
    makes: [],
    models: [],
    modelYears: [],
  };
}

export function hasAnyCriteria(criteria: IssueFilterCriteria): boolean {
  return !!criteria.text
    || criteria.reopenedOnly
    || criteria.includeUnassigned
    || criteria.fingerprints.length > 0
    || criteria.categories.length > 0
    || criteria.lifecycleStates.length > 0
    || criteria.severityTiers.length > 0
    || criteria.vehicleSeverityTiers.length > 0
    || criteria.locationStates.length > 0
    || criteria.homeLocationGroupIds.length > 0
    || criteria.currentShopGroupIds.length > 0
    || criteria.assignedLocationIds.length > 0
    || criteria.assigneeUserIds.length > 0
    || criteria.origins.length > 0
    || criteria.makes.length > 0
    || criteria.models.length > 0
    || criteria.modelYears.length > 0;
}

// The criteria dimensions that exist only because a case exists. A tenant without the
// cases feature has no assignee, so a criteria object still carrying one would narrow the
// sheet by a dimension the screen offers no control to see or clear.
export function withoutCaseCriteria(criteria: IssueFilterCriteria): IssueFilterCriteria {
  return {
    ...criteria,
    assigneeUserIds: [],
    assignedLocationIds: [],
    includeUnassigned: false,
  };
}

// The orders the sheet's column headers offer. Each key names the column it sorts rows by.
// Groups keep their own order: by label, or by rank for severity and lifecycle, and a
// repeated click on the active grouping pill reverses it.
export const IssueSortKeys = {
  Severity: 'severity', Asset: 'asset', Location: 'location', Problem: 'problem',
  Lifecycle: 'lifecycle', Signals: 'signals', Confidence: 'confidence', Age: 'age',
  Assignee: 'assignee',
} as const;
export type IssueSortKey = typeof IssueSortKeys[keyof typeof IssueSortKeys];

// The keys whose value is a name. Rows under one of these open ascending, A to Z; the
// measures open descending, largest first.
export const NAME_SORT_KEYS: ReadonlyArray<IssueSortKey> = [
  IssueSortKeys.Asset, IssueSortKeys.Location, IssueSortKeys.Problem, IssueSortKeys.Assignee,
];

export interface IssueSort {
  key: IssueSortKey;
  dir: 'asc' | 'desc';
}

// A zeroed count for every dimension. Declared once, beside the interface: two literals
// of this shape drift, and a dimension missing from one of them renders as undefined
// rather than as a zero.
export function emptyFacetCounts(): IssueFacetCounts {
  return {
    severityTiers: {}, vehicleSeverityTiers: {}, lifecycleStates: {}, locationStates: {},
    origins: {}, fingerprints: {}, categories: {},
    homeLocationGroupIds: {}, currentShopGroupIds: {}, assignedLocationIds: {},
    assignees: {}, makes: {}, models: {}, modelYears: {}, reopened: 0,
  };
}

// The order the sheet holds unless a saved view names another: worst severity first, then
// oldest open first.
export function defaultSort(): IssueSort {
  return { key: IssueSortKeys.Severity, dir: 'desc' };
}

// Counts per dimension, each computed with its own dimension released from the filter.
// That is what lets a user triage with them: the number beside a value answers "how
// many would I have if I picked this", not "how many do I have now".
export interface IssueFacetCounts {
  severityTiers: Record<number, number>;
  vehicleSeverityTiers: Record<number, number>;
  lifecycleStates: Record<string, number>;
  locationStates: Record<string, number>;
  origins: Record<string, number>;
  fingerprints: Record<string, number>;
  categories: Record<string, number>;
  homeLocationGroupIds: Record<string, number>;
  currentShopGroupIds: Record<string, number>;
  assignedLocationIds: Record<string, number>;
  // Keyed by user id; the empty string counts the unassigned.
  assignees: Record<string, number>;
  makes: Record<string, number>;
  models: Record<string, number>;
  modelYears: Record<string, number>;
  reopened: number;
}

// What the sheet groups by. `none` is one ungrouped run of rows, which is the plain list.
export const IssueGroup = {
  None: 'none', HomeLocation: 'homeLocation', Problem: 'problem',
  Category: 'category', Assignee: 'assignee', Severity: 'severity', Lifecycle: 'lifecycle',
} as const;
export type IssueGroupKey = typeof IssueGroup[keyof typeof IssueGroup];

// Grouping by assignee goes the same way as the assignee criteria, and for the same
// reason: without cases nobody is assigned, so every row lands in one unnamed group.
export function withoutCaseGrouping(key: IssueGroupKey): IssueGroupKey {
  return key === IssueGroup.Assignee ? IssueGroup.None : key;
}

export interface IssueGroupHeader {
  // The raw value: a group id, a fingerprint, a user id, a tier, a state. The screen
  // resolves it to a name; the store holds no reference data.
  value: string;
  count: number;
  // A name taken from the group's own first row, where the row carries one. Rows sharing
  // a fingerprint can disagree about the display name, so the label has to be drawn from
  // inside the group rather than from a map keyed by fingerprint — otherwise whichever
  // row arrived last names the group.
  labelHint?: string;
  // What the board tile carried. A header with only a count answers less than the tiles it
  // replaced, so it carries the same two facts: how long the oldest has been open, and how
  // the group's severity is spread.
  oldestOpenedAtUtc?: string;
  // Rows per tier, 4 critical down to 0 none.
  severityMix?: Record<number, number>;
}

// The sheet renders one flat run of headers and rows, so collapsing a group is a change
// to this index rather than to the rows. A header and a row are different heights, which
// is the reason the viewport measures rather than multiplies.
export type IssueFlatItem =
  | { kind: 'header'; group: IssueGroupHeader; collapsed: boolean }
  | { kind: 'row'; vm: IssueFleetRowVm; group: string };

export interface IssueQueryStore {
  // Replaces the resident set wholesale, for a first load out of IndexedDB.
  replaceAll(rows: IssueSyncRow[]): void;

  // Applies what a delta changed. Called with the ids the replica reports, so the cost
  // tracks the size of the delta rather than the size of the store.
  applyDelta(upserted: IssueSyncRow[], removedIds: string[]): void;

  setCriteria(criteria: IssueFilterCriteria): void;
  setSort(sort: IssueSort): void;

  // How many rows match the current criteria.
  matchCount(): number;

  // How many rows another criteria would match. A filter only: it sorts nothing and leaves
  // the current view in place, so a count per saved view costs one pass over the rows.
  countMatching(criteria: IssueFilterCriteria): number;

  // How many rows are resident, whatever the filter. The denominator in "showing X of Y".
  residentCount(): number;

  idsForRange(start: number, end: number): string[];

  // View-models for one rendered range only.
  rowsForRange(start: number, end: number): IssueFleetRowVm[];

  facets(): IssueFacetCounts;

  rowById(issueId: string): IssueSyncRow | null;

  setGrouping(key: IssueGroupKey, direction?: 'asc' | 'desc'): void;

  // Collapsed groups by value. Held here rather than on the screen because the flat index
  // cannot be built without knowing which groups contribute rows.
  setCollapsed(values: Iterable<string>): void;

  groups(): IssueGroupHeader[];

  // Every id one group holds, in render order. The group header's checkbox claims the
  // whole group, including the rows the window has not rendered yet.
  idsForGroup(value: string): string[];

  // How many items the sheet renders: headers plus the rows of open groups.
  flatLength(): number;

  // Items for one rendered range only. Same rule as rowsForRange — nothing here may
  // return the whole set.
  flatForRange(start: number, end: number): IssueFlatItem[];
}
